/**
 * Settings Page
 * Household-level settings shared by all family members:
 * - Home Address (account identifier)
 * - Parking Registration API configuration
 * - Change Token (household password)
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { updateUser } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ParkingApiHelp from '../components/ParkingApiHelp';

const SettingsPage = () => {
  const { user, refresh } = useAuth();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        tenant: user.tenant || '',
        parking_api_url: user.parking_api_url || '',
        parking_portal_url: user.parking_portal_url || '',
        parking_tenant: user.parking_tenant || '',
        parking_token: user.parking_token || '',
        parking_params: Array.isArray(user.parking_params) ? user.parking_params : [],
        new_token: '',
        confirm_new_token: '',
      });
    }
  }, [user]);

  const updateParam = (index, field, value) => {
    const params = [...(formData.parking_params || [])];
    params[index] = { ...params[index], [field]: value };
    setFormData({ ...formData, parking_params: params });
  };

  const addParam = () => {
    setFormData({
      ...formData,
      parking_params: [...(formData.parking_params || []), { key: '', value: '', changing: false }],
    });
  };

  const removeParam = (index) => {
    setFormData({
      ...formData,
      parking_params: (formData.parking_params || []).filter((_, i) => i !== index),
    });
  };

  if (!user) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setSuccess('');
  };

  const validate = () => {
    const errs = {};
    if (!formData.tenant?.trim()) {
      errs.tenant = 'Home address is required';
    }
    const seenKeys = new Set();
    (formData.parking_params || []).forEach((p, i) => {
      const key = (p.key || '').trim();
      if (!key) {
        errs.params = `Parameter #${i + 1} is missing a key`;
      } else if (seenKeys.has(key.toLowerCase())) {
        errs.params = `Duplicate parameter key "${key}"`;
      }
      seenKeys.add(key.toLowerCase());
    });
    if (formData.new_token) {
      if (formData.new_token.length < 4) {
        errs.new_token = 'Token must be at least 4 characters';
      } else if (formData.new_token !== formData.confirm_new_token) {
        errs.confirm_new_token = 'Tokens do not match';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSuccess('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = { ...formData };
      if (!payload.new_token) delete payload.new_token;
      delete payload.confirm_new_token;
      // Drop empty param rows before saving
      payload.parking_params = (payload.parking_params || []).filter((p) => (p.key || '').trim());
      await updateUser(user.id, payload);
      await refresh();
      setFormData((prev) => ({ ...prev, new_token: '', confirm_new_token: '' }));
      setSuccess('Settings saved');
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <h2><i className="bi bi-gear me-2"></i>Household Settings</h2>
          <p className="text-muted">
            Shared by all family members of <strong>{user.tenant}</strong>
          </p>
        </Col>
      </Row>

      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
      {errors.submit && <Alert variant="danger" dismissible onClose={() => setErrors({ ...errors, submit: '' })}>{errors.submit}</Alert>}

      <Form onSubmit={handleSave}>
        <Row className="mb-4">
          <Col lg={6} className="mb-3">
            <Card className="h-100">
              <Card.Header>Home Address</Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Label>Home Address *</Form.Label>
                  <Form.Control
                    type="text"
                    name="tenant"
                    value={formData.tenant || ''}
                    onChange={handleChange}
                    isInvalid={!!errors.tenant}
                  />
                  <Form.Control.Feedback type="invalid">{errors.tenant}</Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Identifies your household — everyone logs in with this address and the token
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6} className="mb-3">
            <Card className="h-100">
              <Card.Header>Change Token</Card.Header>
              <Card.Body>
                <Form.Text className="text-muted small d-block mb-3">
                  The token is the household password — leave empty to keep the current token.
                </Form.Text>
                <Form.Group className="mb-3">
                  <Form.Label>New Token</Form.Label>
                  <Form.Control
                    type="password"
                    name="new_token"
                    value={formData.new_token || ''}
                    onChange={handleChange}
                    isInvalid={!!errors.new_token}
                  />
                  <Form.Control.Feedback type="invalid">{errors.new_token}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Confirm New Token</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirm_new_token"
                    value={formData.confirm_new_token || ''}
                    onChange={handleChange}
                    isInvalid={!!errors.confirm_new_token}
                  />
                  <Form.Control.Feedback type="invalid">{errors.confirm_new_token}</Form.Control.Feedback>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>Parking Registration API</Card.Header>
              <Card.Body>
                <ParkingApiHelp />
                {errors.params && <Alert variant="danger" className="py-2">{errors.params}</Alert>}
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Registration API URL (DNS name)</Form.Label>
                      <Form.Control
                        type="text"
                        name="parking_api_url"
                        value={formData.parking_api_url || ''}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Portal URL</Form.Label>
                      <Form.Control
                        type="text"
                        name="parking_portal_url"
                        value={formData.parking_portal_url || ''}
                        onChange={handleChange}
                        placeholder="https://thehadley.parkingattendant.com"
                      />
                      <Form.Text className="text-muted">
                        Your building's parking portal — sent as the origin/referer headers.
                        Find it in the browser address bar when registering on the portal.
                        Leave empty to use the default (metro.parkingattendant.com).
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tenant</Form.Label>
                      <Form.Control
                        type="text"
                        name="parking_tenant"
                        value={formData.parking_tenant || ''}
                        onChange={handleChange}
                        placeholder="Building's tenant ID"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Token</Form.Label>
                      <Form.Control
                        type="text"
                        name="parking_token"
                        value={formData.parking_token || ''}
                        onChange={handleChange}
                        placeholder="Building's token"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Text className="text-muted small d-block mb-3">
                  Tenant and Token identify your building with the parking provider
                  (from the portal request, e.g. tenant=5128). Leave empty to use
                  the server defaults.
                </Form.Text>

                <Form.Label className="fw-bold">Parameters (key-value pairs)</Form.Label>
                <Form.Text className="text-muted small d-block mb-2">
                  Sent with every registration. Toggle <strong>"Ask each time"</strong> for values
                  that should be entered per registration (e.g. a parking space that changes);
                  the value here is used as the default.
                </Form.Text>
                {(formData.parking_params || []).map((param, index) => (
                  <Row key={index} className="align-items-center g-2 mb-2">
                    <Col xs={5} md={3}>
                      <Form.Control
                        type="text"
                        placeholder="Key (e.g. space)"
                        value={param.key || ''}
                        onChange={(e) => updateParam(index, 'key', e.target.value)}
                      />
                    </Col>
                    <Col xs={5} md={4}>
                      <Form.Control
                        type="text"
                        placeholder="Value"
                        value={param.value || ''}
                        onChange={(e) => updateParam(index, 'value', e.target.value)}
                      />
                    </Col>
                    <Col xs={6} md={3} className="d-flex align-items-center">
                      <Form.Check
                        type="switch"
                        id={`changing-${index}`}
                        label="Ask each time"
                        checked={!!param.changing}
                        onChange={(e) => updateParam(index, 'changing', e.target.checked)}
                      />
                    </Col>
                    <Col xs={6} md={2} className="text-md-end">
                      <Button variant="outline-danger" size="sm" onClick={() => removeParam(index)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button variant="outline-secondary" size="sm" onClick={addParam} className="mt-1">
                  <i className="bi bi-plus-lg me-1"></i> Add Parameter
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting ? 'Saving...' : 'Save Settings'}
        </Button>
      </Form>
    </Container>
  );
};

export default SettingsPage;
